import styles from './settingsTabs.module.scss'
import { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { updateProfile } from '../../features/authSlice'
import { FormInput } from '../input/FormInput'
import { Avatar, Box } from '@mui/material'
import { toastHelper } from '../../util/toastHelper'
import ButtonCustom from '../atoms/button/ButtonCustom'
import GoogleAddressInput from '../atoms/input/GoogleAddressInput'
import { STAFF_ROLES } from '../../util/constants'

const businessAccounts = ['vendor', 'kazoo-planner']
// Component for editing user information: full name, preferred name, and profile picture
export const InformationSettings = () => {
	const dispatch = useDispatch()

	// Get current user info from Redux store
	const user = useSelector((state) => state.auth.user)
	const accountType = useSelector((state) => state.auth.accountType)

	// Local state for form toggle and user info
	const [save, setSave] = useState(false)
	const [formData, setFormData] = useState({
		firstName: '',
		lastName: '',
		profilePic: null,
		preferredName: '',
		businessName: '',
		businessAddress: '',
	})

	// Prefill form fields with user data when user is available
	useEffect(() => {
		if (user) {
			setFormData({
				firstName: user.firstName,
				lastName: user.lastName,
				profilePic: user.profilePic,
				preferredName: user.preferredName,
				businessName: user.businessName,
				businessAddress: user.businessAddress,
			})
		}
	}, [
		user?.firstName,
		user?.lastName,
		user?.profilePic,
		user?.preferredName,
		user?.businessName,
		user?.businessAddress,
	])

	const handlePlaceSelected = (placeData) => {
		setFormData(prev => ({
			...prev,
			businessAddress: placeData.formattedAddress
		}))
	}

	// Handle submission of updated user info
	const handleSubmit = (e) => {
		e.preventDefault()
		if (save) {
			dispatch(updateProfile(formData))
		}
		// Toggle between edit and save state
		setSave((prev) => !prev)
	}

	// Handle uploading of profile picture
	const handleFileChange = (e) => {
		const file = e.target.files[0]
		if (!file) return

		const maxSizeMB = 4 //Set the img size
		const maxSizeInBytes = maxSizeMB * 1024 * 1024

		if (file?.size > maxSizeInBytes) {
			return toastHelper(
				'Image size too large, please choose images less than 4mb',
				'error'
			)
		}
		const fileURL = URL.createObjectURL(file)

        setFormData({
            ...formData,
            containsImage: 'profilePic',
            profilePic: fileURL,
            imageMediaMap: { [fileURL]: file }
        })
		
	}

	return (
		<div className={styles.profileCard}>
			<h1 className={styles.boxTitle}>Information Settings</h1>
			{/* Display user role */}
			<form onSubmit={handleSubmit} className={styles.formBox}>
				{/* Profile picture upload and preview area */}
				<div>
					<label
						htmlFor="profileUpload"
						className={styles.uploadCircle}
					>
						{formData.profilePic ? (
							<Avatar
								src={formData.profilePic}
								sx={{ width: '100%', height: '100%' }}
							/>
						) : (
							<span>
								Upload
								<br />A Photo
							</span>
						)}
						{save && (
							<div className={styles.cameraIcon}>
								<img src="images/camera.svg" alt="" />
							</div>
						)}
					</label>
					<input
						disabled={!save}
						id="profileUpload"
						type="file"
						accept=".jpg, .png"
						style={{ display: 'none' }}
						onChange={handleFileChange}
					/>
				</div>
				<Box className={styles.formInputs}>
					<FormInput
						type="text"
						name="firstName"
						placeholder="First Name"
						value={formData.firstName}
						setDataFunction={setFormData}
						data={formData}
						required={true}
						disabled={!save || STAFF_ROLES.includes(accountType)}
						data_cy="firstName"
					/>
					<FormInput
						type="text"
						name="lastName"
						placeholder="Last Name"
						value={formData.lastName}
						setDataFunction={setFormData}
						data={formData}
						required={true}
						disabled={!save || STAFF_ROLES.includes(accountType)}
						data_cy="lastName"
					/>
					{accountType === 'user' && (
						<FormInput
							type="text"
							name="preferredName"
							placeholder="Preferred Name"
							value={formData.preferredName}
							setDataFunction={setFormData}
							data={formData}
							disabled={!save}
							data_cy="preferredName"
						/>
					)}
					{businessAccounts.includes(accountType) && (
						<>
							<FormInput
								type="text"
								name="businessName"
								placeholder="Business Name"
								value={formData.businessName}
								setDataFunction={setFormData}
								data={formData}
								required={true}
								disabled={!save}
								data_cy="businessName"
							/>
							<GoogleAddressInput
								data_cy="businessAddress"
								placeholder="Business Address"
								name="businessAddress"
								value={formData.businessAddress}
								setDataFunction={setFormData}
								data={formData}
								required={true}
								onPlaceSelected={handlePlaceSelected}
								disabled={!save}
								showLabel={true}
							/>
						</>
					)}

					{STAFF_ROLES.includes(accountType) && (
						<p>
							As a staff member, you cannot change your first and
							last name. Please notify your business if you wish
							to do so
						</p>
					)}
				</Box>
				{/* Button toggles between Edit and Save modes */}
				<Box className={styles.buttonContainer}>
					{save && (
						<ButtonCustom
							variant="outlined"
							type="button"
							onClick={() => {
                                setFormData({
                                    firstName: user.firstName,
                                    lastName: user.lastName,
                                    profilePic: user.profilePic,
                                    preferredName: user.preferredName,
                                    businessName: user.businessName,
                                    businessAddress: user.businessAddress,
                                })
                                setSave(false)
                            }}
						>
							Cancel
						</ButtonCustom>
					)}
					<ButtonCustom type="submit">
						{save ? 'Update' : 'Edit'}
					</ButtonCustom>
				</Box>
			</form>
		</div>
	)
}
